import { useState, useMemo } from 'react';
import React from 'react';
import { Combobox, Button, useCombobox, UnstyledButton, Text, ScrollArea } from '@mantine/core';
import { IconSettings } from '@tabler/icons-react';

type Item = {
  id: string;
  name: string;
};

type ItemComboboxProps<T extends Item> = {
  items: T[];
  selectedItems: T[];
  onItemsChange: (newItems: string[]) => void;
  onManageItems?: () => void;
  itemName?: string;
  renderItem: (item: T, selected: boolean) => React.ReactNode;
  trigger?: React.ReactNode;
  selectedLabel?: string;
  unselectedLabel?: string;
};

export function ItemCombobox<T extends Item>({
  items,
  selectedItems,
  onItemsChange,
  onManageItems,
  itemName = 'item',
  renderItem,
  trigger,
  selectedLabel = 'Selected',
  unselectedLabel = 'Available',
}: ItemComboboxProps<T>) {
  const combobox = useCombobox({
    onDropdownClose: () => {
      setSearch('');
      combobox.resetSelectedOption();
    },
    onDropdownOpen: () => {
      combobox.focusSearchInput();
      combobox.updateSelectedOptionIndex('active');
    },
  });

  const [search, setSearch] = useState('');

  const handleValueSelect = (val: string) => {
    combobox.closeDropdown();
    if (val === 'manage-items' && onManageItems) {
      onManageItems();
    } else {
      const currentItems = selectedItems.map((item) => item.id);
      const newValue = currentItems.includes(val) ? currentItems.filter((v) => v !== val) : [...currentItems, val];

      onItemsChange(newValue);
    }
  };

  const selectedItemIds = useMemo(() => new Set(selectedItems.map((item) => item.id)), [selectedItems]);

  const options = useMemo(
    () =>
      items
        .filter((item) => item.name.toLowerCase().includes(search.toLowerCase()))
        .map((item) => {
          const isSelected = selectedItemIds.has(item.id);
          return (
            <Combobox.Option
              value={item.id}
              key={item.id}
              active={isSelected}
            >
              {renderItem(item, isSelected)}
            </Combobox.Option>
          );
        }),
    [items, search, selectedItemIds, renderItem],
  );

  // if trigger is not provided, we display the selected items as the trigger for the combobox
  const values = useMemo(
    () =>
      selectedItems
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((item) => <div key={item.id}>{renderItem(item, true)}</div>),
    [selectedItems, renderItem],
  );

  const selectedOptions = useMemo(
    () => options.filter((option) => selectedItemIds.has(option.props.value)),
    [options, selectedItemIds],
  );
  const unselectedOptions = useMemo(
    () => options.filter((option) => !selectedItemIds.has(option.props.value)),
    [options, selectedItemIds],
  );

  return (
    <Combobox
      store={combobox}
      onOptionSubmit={handleValueSelect}
      width={200}
      position="bottom-start"
      transitionProps={{ transition: 'pop-top-left' }}
      keepMounted={false}
    >
      <Combobox.DropdownTarget>
        {trigger ?
          React.cloneElement(trigger as React.ReactElement, {
            // @ts-expect-error - prob should fix this
            onClick: () => combobox.toggleDropdown(),
          })
        : selectedItems.length > 0 ?
          <UnstyledButton
            className="flex flex-wrap gap-1"
            onClick={() => combobox.toggleDropdown()}
          >
            {values}
          </UnstyledButton>
        : <Button
            className="opacity-60 transition-all hover:opacity-100"
            color="gray"
            variant="subtle"
            size="compact-xs"
            onClick={() => combobox.toggleDropdown()}
          >
            {`+ Add ${itemName}`}
          </Button>
        }
      </Combobox.DropdownTarget>
      <Combobox.Dropdown>
        <Combobox.Search
          placeholder={`Search ${itemName}...`}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {options?.length === 0 && <Combobox.Empty>Nothing found</Combobox.Empty>}
        <Combobox.Options>
          <ScrollArea.Autosize
            mah={250}
            scrollbarSize={6}
          >
            {selectedOptions.length > 0 && <Combobox.Group label={selectedLabel}>{selectedOptions}</Combobox.Group>}
            {unselectedOptions.length > 0 && (
              <Combobox.Group label={unselectedLabel}>{unselectedOptions}</Combobox.Group>
            )}
          </ScrollArea.Autosize>
          {!!onManageItems && (
            <>
              <div className="my-1 w-full border-t border-[var(--mantine-color-gray-2)] dark:border-[var(--mantine-color-dark-4)]" />
              <Combobox.Option
                value="manage-items"
                className="flex items-center gap-1"
              >
                <IconSettings size={14} />
                <Text size="sm">{`Manage ${itemName}s`}</Text>
              </Combobox.Option>
            </>
          )}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
}
